import type {
  GitHubAppConfigResponse,
  GitHubInstallationSummary,
  RunStatusSnapshotForSettings
} from './helpers/runScreenTypes';

export function RunSettingsScreen({
  status,
  runId,
  githubSessionId,
  githubConfig,
  githubInstallations,
  selectedGithubInstallationId,
  isLogoutDisabled,
  logoutError,
  onConnectGitHub,
  onInstallGitHubApp,
  onLogout
}: {
  status: RunStatusSnapshotForSettings | undefined;
  runId: string | null;
  githubSessionId: string;
  githubConfig: GitHubAppConfigResponse | null;
  githubInstallations: GitHubInstallationSummary[];
  selectedGithubInstallationId: number | null;
  isLogoutDisabled: boolean;
  logoutError: string;
  onConnectGitHub: () => void;
  onInstallGitHubApp: () => void;
  onLogout: () => void;
}) {
  const scope = status?.scopePaths?.length
    ? status.scopePaths.join(', ')
    : status?.scopePath ?? 'Full codebase';
  const selectedGithubInstallation = githubInstallations.find((installation) => {
    return installation.id === selectedGithubInstallationId;
  });
  const hasGitHubInstallUrl = Boolean(githubConfig?.installUrl ?? selectedGithubInstallation?.htmlUrl);
  const hasGitHubInstallation = githubInstallations.length > 0;

  return (
    <main className="run-page-panel">
      <div className="run-panel-heading">
        <h1>Settings</h1>
      </div>

      <section className="run-settings-list" aria-label="Run settings">
        {runId && (
          <>
            <div className="run-settings-row">
              <div>
                <strong>Run ID</strong>
                <span>{runId}</span>
              </div>
            </div>
            <div className="run-settings-row">
              <div>
                <strong>Target</strong>
                <span>{status?.target ?? 'Loading'}</span>
              </div>
            </div>
            <div className="run-settings-row">
              <div>
                <strong>Scope</strong>
                <span>{scope}</span>
              </div>
            </div>
            <div className="run-settings-row">
              <div>
                <strong>Status</strong>
                <span>{status?.status ?? 'Loading'}</span>
              </div>
            </div>
          </>
        )}
        <div className="run-settings-row">
          <div>
            <strong>GitHub App</strong>
            <span>{hasGitHubInstallation ? 'Installed' : githubSessionId ? 'Not installed' : 'Not connected'}</span>
          </div>
          <button
            type="button"
            className="secondary-button run-panel-action"
            onClick={hasGitHubInstallation || githubSessionId ? onInstallGitHubApp : onConnectGitHub}
            disabled={githubSessionId ? !hasGitHubInstallUrl : !githubConfig?.connectUrl}
          >
            {hasGitHubInstallation ? 'Manage access' : githubSessionId ? 'Install app' : 'Login with GitHub'}
          </button>
        </div>
        <div className="run-settings-row">
          <div>
            <strong>Account</strong>
            <span>{githubSessionId ? 'Logged in with GitHub' : 'Not logged in'}</span>
            {logoutError && <span className="run-settings-error" role="alert">{logoutError}</span>}
          </div>
          <button
            type="button"
            className="secondary-button run-panel-action"
            onClick={onLogout}
            disabled={!githubSessionId || isLogoutDisabled}
          >
            {isLogoutDisabled ? 'Logging out...' : 'Logout'}
          </button>
        </div>
      </section>
    </main>
  );
}
